import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { SimpleCopyCell } from '@/src/components/manage/tables/CopyableCell'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { listUsers } from '@/src/api/user'
import { useTranslation } from '@/src/i18n/client'

type UserWithDetails = Awaited<ReturnType<typeof listUsers>>[number]

export const Route = createFileRoute('/_manage/manage/admin/users/')({
  component: AdminUsersPage,
})

function AdminUsersPage() {
  const { t } = useTranslation();
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['user', 'list'],
    queryFn: () => listUsers(),
  })

  const columns: ColumnConfig<UserWithDetails>[] = [
    {
      key: 'id',
      label: t('table.id'),
      width: 'w-24',
      render: (user) => <SimpleCopyCell value={user.id} truncateLength={20} />,
    },
    {
      key: 'name',
      label: t('table.name'),
      render: (user) => user.name || '-',
    },
    {
      key: 'email',
      label: t('table.email'),
    },
    {
      key: 'role',
      label: t('table.role'),
      render: (user) => user.role ? (user.role.name || user.role.role) : '-',
    },
    {
      key: 'organization',
      label: t('table.organization'),
      render: (user) => user.organization ? user.organization.name : '-',
    },
  ];

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction
            to="/manage/admin/users/new"
            label="Add User"
            size="sm"
          />
        </AdminToolbar.Left>
      </AdminToolbar>
      <div>
        <DataTable<UserWithDetails>
          data={users || []}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage="No users found."
          actions={(user) => (
            <EditAction
              to="/manage/admin/users/$id"
              params={{ id: user.id }}
            />
          )}
        />
      </div>
    </div>
  )
}
